import { apiFetch, apiUpdate, type RequestOptions } from './client'
import { relatedId } from './documents'
import { type Household, withinHousehold } from './households'
import {
  type Document,
  type Resource,
  type ResourceUpdate,
  SHIPPING_ADDRESSES_TYPE,
  SUBSCRIPTIONS_TYPE,
  type SubscriptionAttributes,
  type SubscriptionCadence,
  type SubscriptionStatus,
  type SubscriptionTier,
} from './types'

/**
 * A child's subscription as the components see it. Shopify and Recharge are
 * the source of truth: the platform records the plan and status, reads the
 * rest from Recharge when one subscription is asked for, and makes every
 * change there before recording it.
 */
export interface Subscription {
  id: string
  tier: SubscriptionTier
  cadence: SubscriptionCadence
  status: SubscriptionStatus
  /** The child it is for. */
  userId: string
  /**
   * The day Recharge next charges (and ships the kit), as YYYY-MM-DD. Null in
   * a list, once cancelled, and until Recharge has reported the subscription.
   */
  nextChargeOn: string | null
  /**
   * The recurring price as the API formats it, before tax and shipping. Null
   * in a list, and until Recharge has reported the subscription.
   */
  price: string | null
}

export const CANCELLATION_REASONS = [
  'too_expensive',
  'not_using',
  'child_lost_interest',
  'moving',
  'other',
] as const

export type CancellationReason = (typeof CANCELLATION_REASONS)[number]

/**
 * What a household owner may ask for. Everything else about a subscription
 * is Recharge's to change. A pause is until a day of the owner's choosing; a
 * resume of a paused subscription charges tomorrow, and of a cancelled one
 * activates it again.
 */
export type SubscriptionChange =
  | { status: 'cancelled'; reason: CancellationReason; comments?: string }
  | { status: 'paused'; resumeOn: string }
  | { status: 'active' }
  | { skipNextCharge: true }
  | { shippingAddressId: string }

export function toSubscription(resource: Resource): Subscription {
  const attributes = resource.attributes as SubscriptionAttributes | undefined
  if (!attributes) throw new Error(`Subscription ${resource.id} came back without attributes`)

  const userId = relatedId(resource, 'user')
  if (!userId) throw new Error(`Subscription ${resource.id} came back without its child`)

  return {
    id: resource.id,
    tier: attributes.tier,
    cadence: attributes.cadence,
    status: attributes.status,
    userId,
    nextChargeOn: attributes.next_charge_on ?? null,
    price: attributes.price ?? null,
  }
}

/** Every subscription of the household's children, newest first, with nothing that needs Recharge. */
export async function fetchSubscriptions(
  household: Household,
  options: RequestOptions = {},
): Promise<Subscription[]> {
  const document = await apiFetch<Document<Resource[]>>(
    `/organizations/${household.id}/subscriptions`,
    withinHousehold(household, options),
  )
  return document.data.map(toSubscription)
}

/** One subscription, with what Recharge will next charge for it, and when. */
export async function fetchSubscription(
  id: string,
  options: RequestOptions = {},
): Promise<Subscription> {
  const document = await apiFetch<Document<Resource>>(`/subscriptions/${id}`, options)
  return toSubscription(document.data)
}

/** The document `PUT /subscriptions/x` takes for a change. */
export function toSubscriptionUpdate(id: string, change: SubscriptionChange): ResourceUpdate {
  const type = SUBSCRIPTIONS_TYPE

  if ('shippingAddressId' in change) {
    return {
      type,
      id,
      relationships: {
        shipping_address: {
          data: { type: SHIPPING_ADDRESSES_TYPE, id: change.shippingAddressId },
        },
      },
    }
  }

  if ('skipNextCharge' in change) return { type, id, attributes: { skip_next_charge: true } }

  switch (change.status) {
    case 'cancelled':
      return {
        type,
        id,
        attributes: {
          status: 'cancelled',
          cancellation_reason: change.reason,
          ...(change.comments && { cancellation_reason_comments: change.comments }),
        },
      }
    case 'paused':
      return { type, id, attributes: { status: 'paused', resume_on: change.resumeOn } }
    case 'active':
      return { type, id, attributes: { status: 'active' } }
  }
}

/**
 * Ask Recharge for a change, through the platform. The subscription that
 * comes back is the one Recharge has now, with its next charge.
 */
export async function updateSubscription(
  id: string,
  change: SubscriptionChange,
  options: RequestOptions = {},
): Promise<Subscription> {
  const document = await apiUpdate<Document<Resource>>(
    `/subscriptions/${id}`,
    toSubscriptionUpdate(id, change),
    options,
  )
  return toSubscription(document.data)
}
